import { useCallback, useContext, useMemo } from 'react'
import { AgxFileUpload } from '@urbanx/agx-ui-components'
import {
  DocumentTypes,
  UploadedDocument,
  UploadedDocumentDocumentType,
} from '@urbanx/agx.campaigns.client'
import { AuthContext } from '@urbanx/agx.auth.client'
import { AxiosProgressEvent, GenericAbortSignal } from 'axios'
import { getFileLink, uploadFile } from 'services'
import { AgxToastState } from 'types/commonTypes'
import { buildContractFileName } from './contractCompliance'
import { useSelectedCampaign } from 'hooks'

interface ContractUploadStepProps {
  uploadedDocument?: UploadedDocument
  updateToastState: (toastState: AgxToastState) => void
  deleteFile: (
    authToken: string,
    filePath: string
  ) => Promise<number | undefined>
  onFileChanged: (change: { value?: UploadedDocument[] }) => void
  onUploadingChange: (isUploading: boolean) => void
}

const ContractUploadStep = ({
  uploadedDocument,
  updateToastState,
  deleteFile,
  onFileChanged,
  onUploadingChange,
}: ContractUploadStepProps) => {
  const { getAuthToken } = useContext(AuthContext)
  const { campaign: selectedCampaign } = useSelectedCampaign()
  const fileName = useMemo(
    () =>
      buildContractFileName(
        selectedCampaign?.address?.streetAddress,
        selectedCampaign?.address?.suburb
      ),
    [selectedCampaign?.address]
  )

  const filePath = useMemo(
    () =>
      selectedCampaign?.campaignId
        ? `${selectedCampaign.campaignId}/ListingDocuments/${fileName}`
        : undefined,
    [selectedCampaign?.campaignId, fileName]
  )

  const buildRenamedFormData = useCallback(
    (formData: FormData, name: string) => {
      const file = formData.get('File')
      if (!file || typeof File === 'undefined' || !(file instanceof File)) {
        return formData
      }

      const renamedFile = new File([file], name, {
        type: file.type,
        lastModified: file.lastModified,
      })
      const renamedFormData = new FormData()
      renamedFormData.append('File', renamedFile)
      return renamedFormData
    },
    []
  )

  const uploadContractFile = useCallback(
    async (
      authToken: string,
      file: string | FormData,
      uploadCampaignId: string,
      uploadedFormType: DocumentTypes,
      uploadProgress?: (progressEvent: AxiosProgressEvent) => void,
      abortSignal?: GenericAbortSignal
    ) => {
      onUploadingChange(true)
      try {
        const renamedFormData =
          typeof FormData !== 'undefined' && file instanceof FormData
            ? buildRenamedFormData(file, fileName)
            : file

        return await uploadFile(
          authToken,
          renamedFormData,
          uploadCampaignId,
          uploadedFormType,
          uploadProgress ?? null,
          abortSignal ?? null,
          filePath
        )
      } catch {
        updateToastState({
          color: 'error',
          message: 'Error uploading contract',
          open: true,
        })
        return undefined
      } finally {
        onUploadingChange(false)
      }
    },
    [
      buildRenamedFormData,
      fileName,
      filePath,
      onUploadingChange,
      updateToastState,
    ]
  )

  return (
    <AgxFileUpload
      id="contractUpload"
      title="Upload file"
      buttonText={uploadedDocument ? 'Replace file' : 'Choose file'}
      fileData={uploadedDocument ? [uploadedDocument] : []}
      campaignId={selectedCampaign?.campaignId || ''}
      documentType={UploadedDocumentDocumentType.BlankSalesContract}
      getAuthToken={getAuthToken}
      getFileLink={getFileLink}
      uploadFile={uploadContractFile}
      deleteFile={deleteFile}
      extensions={['pdf']}
      multiple={false}
      onValueChanged={onFileChanged}
    />
  )
}

export default ContractUploadStep
